import { useContext, useEffect, useRef, useState } from 'react';
import {
  ActivityLogDataCtx,
  FilterItem,
  FilterProps,
  FilterRange,
} from 'contexts/activityLog/types';
import { activitiesLogInitialValues } from 'contexts/activityLog/utils';
import { getActivitiesLogAction, getActivityLogUsersAction } from 'contexts/activityLog/actions';
import { SessionContext } from 'contexts/session';
import usePolling from 'hooks/usePolling';
import { unwrapActionResult } from 'constants/errors/codes';

const ACTIVITY_LOG_POLL_INTERVAL_MS = 30000;

interface ActivityLogHook {
  fetching: boolean;
  filters: FilterProps;
  isEmpty: boolean;
  activitiesLogTable: ActivityLogDataCtx['table'];
  activityLogUsers: ActivityLogDataCtx['activityLogUsers'];
  error: string | boolean;
  isPollingCircuitOpen: boolean;
  onChangeFilter: (filter: FilterItem) => void;
  onChangeFilters: (filters: FilterItem[]) => void;
  onChangeRangeFilters: (range: FilterRange) => void;
  onRetryPolling: () => void;
  onRetrieveActivityLogUsers: (account_id?: string) => void;
  onResetActivities: (account_id?: string) => void;
}

const useActivityLogData = (): ActivityLogHook => {
  const [activitiesLogTable, setActivitiesLogTable] = useState(activitiesLogInitialValues.table);
  const [fetching, setFetching] = useState(false);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterProps>(activitiesLogInitialValues.filters);
  const [activityLogUsers, setActivityLogUsers] = useState(
    activitiesLogInitialValues.activityLogUsers
  );
  const { isStaff } = useContext(SessionContext);
  const isMounted = useRef(true);

  const areFiltersEqual = (prevFilters, nextFilters) => {
    const keys = new Set([...Object.keys(prevFilters), ...Object.keys(nextFilters)]);
    for (const key of keys) {
      if (prevFilters[key] !== nextFilters[key]) {
        return false;
      }
    }
    return true;
  };
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const getPaginatedActivities = async ({ params }) => {
    if (isStaff && !params.account_id) return;
    setFetching(true);
    // Cancel any poll tick that's due but hasn't fired yet — otherwise it could still go out
    // to the same endpoint while this fetch is in flight. resetPollingSchedule() below
    // re-arms it once this fetch actually completes.
    clearPollingSchedule();
    try {
      const activitiesLogData = await getActivitiesLogAction(params);
      if (activitiesLogData instanceof Error) {
        if (!isMounted.current) return;
        setActivitiesLogTable((prevState) => ({
          ...prevState,
          error: activitiesLogData?.message,
        }));
      } else {
        if (!isMounted.current) return;
        setActivitiesLogTable((prevState) => {
          return {
            ...prevState,
            count: activitiesLogData?.results?.count,
            items: filters.append_results
              ? [...prevState.items, ...(activitiesLogData?.results?.results ?? [])]
              : activitiesLogData?.results?.results ?? [],
          };
        });
        setIsEmpty((activitiesLogData.results?.results ?? []).length === 0);
      }
      if (!isMounted.current) return;
      setFetching(false);
      // This fetch just covered the list endpoint (mount or a filter change) — time the
      // background poll's next check from here, not from mount, so a slow fetch here and the
      // poll's own timer never end up hitting the endpoint at the same time.
      resetPollingSchedule();
    } catch (error) {
      if (!isMounted.current) return;
      setFetching(false);
      resetPollingSchedule();
    }
  };

  const {
    isCircuitOpen: isPollingCircuitOpen,
    retry: onRetryPolling,
    resetSchedule: resetPollingSchedule,
    clearSchedule: clearPollingSchedule,
  } = usePolling(
    () => {
      // Captured now (at request-issue time) so onSuccess can tell whether the user changed
      // filters while this request was in flight, rather than reading `filters` at response
      // time, which would always see the current (possibly different) value.
      const requestFilters = filters;
      // The background check always looks at page 1 — it's watching for newly-appeared
      // activity, not following whatever page the user happens to be viewing.
      return getActivitiesLogAction({ ...handleReqData(), page: 1 }).then((activitiesLogData) => ({
        activitiesLogData: unwrapActionResult(activitiesLogData, 'activity log poll'),
        requestFilters,
      }));
    },
    {
      intervalMs: ACTIVITY_LOG_POLL_INTERVAL_MS,
      // The initial load and any filter-driven fetch (the useEffect below) already cover
      // this endpoint — see resetPollingSchedule() in getPaginatedActivities.
      manualSchedule: true,
      onSuccess: ({ activitiesLogData, requestFilters }) => {
        if (!isMounted.current) return;
        // Filters changed since this request was issued — the filter change's own fetch
        // already owns the table now, so merging this stale, differently-filtered response
        // in would corrupt it.
        if (requestFilters !== filters) return;
        setActivitiesLogTable((prevState) => {
          const prevCount = prevState.count ?? 0;
          const nextCount = activitiesLogData?.results?.count;
          // A count that dropped (a record was deleted) would make the slice below negative,
          // which prepends rows already on screen as duplicates.
          if (typeof nextCount !== 'number' || nextCount <= prevCount) return prevState;

          const diff = (nextCount ?? 0) - prevCount;
          const newElems = (activitiesLogData?.results?.results ?? []).slice(0, diff);
          return {
            ...prevState,
            count: nextCount,
            items: [...newElems, ...(prevState.items ?? [])],
          };
        });
      },
      onError: (err) => {
        if (!isMounted.current) return;
        setActivitiesLogTable((prevState) => ({
          ...prevState,
          error: (err as Error)?.message,
        }));
      },
    }
  );

  const retrieveActivityLogUsers = async (account_id: string) => {
    const activityLogUsersResponse = account_id
      ? await getActivityLogUsersAction({
          account_id: account_id,
        })
      : await getActivityLogUsersAction();
    if (activityLogUsersResponse instanceof Error) {
      if (!isMounted.current) return;
      setActivityLogUsers([]);
    } else {
      const systemUser = {
        label: 'System',
        value: 'system',
        key: 'system',
      };
      const users = (activityLogUsersResponse?.results ?? []).map(({ id, name }) => {
        return {
          label: name,
          value: id.toString(),
          key: id.toString(),
        };
      });
      if (!isMounted.current) return;
      setActivityLogUsers([systemUser, ...users]);
    }
  };

  const handleReqData = () => {
    return {
      page_size: parseInt(filters['page_size']),
      page: parseInt(filters['page']),
      from_date: filters['from_date'] ? `${filters['from_date']}T00:00:00` : null,
      to_date: filters['to_date'] ? `${filters['to_date']}T23:59:59` : null,
      generated_by: filters['generated_by'] || null,
      types: filters['types'] || null,
      account_id: filters['account_id'] || null,
    };
  };

  const onChangeFilter = (filter: FilterItem) => {
    const name = filter.name.toString();
    const value = String(filter.value ?? '');
    const resetPage = name !== 'page';
    setFilters((prevState) => {
      const nextState = resetPage
        ? {
            ...prevState,
            page: '1',
            [name]: value,
            append_results: false,
          }
        : {
            ...prevState,
            [name]: value,
            append_results: true,
          };

      return areFiltersEqual(prevState, nextState) ? prevState : nextState;
    });
  };

  const onChangeFilters = (filters: FilterItem[]) => {
    const newFilters = filters.reduce(
      (acc, filter) => ({ ...acc, [filter.name]: filter.value }),
      {}
    );
    setFilters((prevState) => {
      const nextState = {
        ...prevState,
        ...newFilters,
      };

      return areFiltersEqual(prevState, nextState) ? prevState : nextState;
    });
  };

  const onChangeRangeFilters = (dates: FilterRange) => {
    const fromName = dates.fromName;
    const fromValue = dates.fromValue;
    const toName = dates.toName;
    const toValue = dates.toValue;

    setFilters((prevState) => {
      const nextState = {
        ...prevState,
        page: '1',
        [fromName]: fromValue,
        [toName]: toValue,
        append_results: false,
      };

      return areFiltersEqual(prevState, nextState) ? prevState : nextState;
    });
  };

  const onRetrieveActivityLogUsers = (account_id?: string) => {
    retrieveActivityLogUsers(account_id);
  };

  const onResetActivities = (account_id?: string) => {
    const filters = [
      { name: 'from_date', value: null },
      { name: 'to_date', value: '' },
      { name: 'generated_by', value: null },
      { name: 'types', value: null },
      { name: 'page', value: 1 },
      { name: 'append_results', value: false },
    ];
    account_id && filters.push({ name: 'account_id', value: account_id });
    onChangeFilters(filters);
  };

  useEffect(() => {
    getPaginatedActivities({ params: handleReqData() });
  }, [filters]);

  return {
    fetching: fetching,
    activitiesLogTable: activitiesLogTable,
    error: false,
    isEmpty,
    filters,
    activityLogUsers,
    isPollingCircuitOpen,
    onChangeFilter,
    onChangeFilters,
    onChangeRangeFilters,
    onRetryPolling,
    onRetrieveActivityLogUsers,
    onResetActivities,
  };
};

export default useActivityLogData;
